import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import { GET_TOP_DIAGNOSES } from '../graphql/operations';
import { TopDiagnosesResponse, DiagnosisCount } from '../types';

const TopDiagnoses: React.FC = () => {
  const [timePeriod, setTimePeriod] = useState(24);

  const { data, loading, error } = useQuery(GET_TOP_DIAGNOSES, {
    variables: { timePeriodHours: timePeriod, limit: 10 },
    pollInterval: 300000,
  });

  const topDiagnoses: TopDiagnosesResponse | null = data?.getTopDiagnoses;
  console.log(topDiagnoses);

  const handleTimePeriodChange = (_: React.SyntheticEvent, newValue: number) => {
    setTimePeriod(newValue);
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">Error loading diagnosis analytics: {error.message}</Alert>;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" component="h2">
            Top Diagnoses
          </Typography>
          {topDiagnoses && (
            <Typography variant="body2" color="text.secondary">
              {topDiagnoses.total_diagnoses.toLocaleString()} total scans
            </Typography>
          )}
        </Box>

        <Tabs 
          value={timePeriod} 
          onChange={handleTimePeriodChange} 
          sx={{ mb: 3 }}
          variant="fullWidth"
        >
          <Tab label="24H" value={24} />
          <Tab label="7D" value={168} />
          <Tab label="30D" value={720} />
        </Tabs>

        {topDiagnoses && topDiagnoses.items.length > 0 ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="60">#</TableCell>
                  <TableCell>Diagnosis</TableCell>
                  <TableCell align="right" width="80">Cases</TableCell>
                  <TableCell align="right" width="80">%</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topDiagnoses.items.map((diagnosis: DiagnosisCount, index: number) => (
                  <TableRow 
                    key={`${diagnosis.disease_name}-${index}`}
                    sx={{ 
                      '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                  >
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight={index < 3 ? 'bold' : 'normal'}
                        color={index === 0 ? 'primary.main' : index === 1 ? 'warning.main' : index === 2 ? 'info.main' : 'text.primary'}
                      >
                        {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={index < 3 ? 'medium' : 'normal'}>
                        {diagnosis.disease_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={index < 3 ? 'medium' : 'normal'}>
                        {diagnosis.count.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body2" 
                        fontWeight={index < 3 ? 'medium' : 'normal'}
                        color={index < 3 ? 'primary.main' : 'text.secondary'}
                      >
                        {diagnosis.percentage.toFixed(1)}%
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No diagnosis data available
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TopDiagnoses;